import { Link, type LinkComponentProps } from "@tanstack/react-router";
import type { ComponentProps } from "react";

/**
 * An in-app link whose destination is a plain path string.
 *
 * TanStack Router types `to` as the union of the generated route paths, which
 * is the point of it: `to="/p/$project/mail"` with `params` is checked. Most
 * links in this app, though, are built from `projectPath(project, suffix)` —
 * one helper that every screen, the nav and the command palette share — and a
 * computed string cannot satisfy that union.
 *
 * So this is the one cast, in one file, rather than a cast per call site. The
 * paths it carries all come from `lib/nav.ts` or from a fixture id, so a typo
 * is caught by the router's own not-found route and by the screenshot suite
 * asserting a 200 and a heading for every screen.
 *
 * Where a link is static and has no computed segment, use `Link` directly and
 * take the typing. Migrating the rest to typed routes is worth doing once the
 * route shapes stop moving; it is a mechanical change confined to call sites.
 */
export type AppLinkProps = Omit<ComponentProps<typeof Link>, "to" | "params"> & {
  to: string;
  search?: LinkComponentProps["search"];
};

export function AppLink({ to, ...props }: AppLinkProps) {
  // biome-ignore lint/suspicious/noExplicitAny: see the note above.
  return <Link to={to as any} {...(props as any)} />;
}
